import { ApplicantType } from '@/helpers/const';
import { resolveMarriedPartnerId } from '@/helpers/resolveMarriedPartnerId';
import type { ApplicantWithRelationships } from '@/types';

/**
 * A household is a married/defacto couple on the loan, or a single applicant on their own.
 * Income and assets are declared per applicant; expenses are declared once per household.
 *
 * Households are resolved from marriage relationship rows rather than from borrower entities:
 * partners can sit under different companies/trusts (a director married to a trustee), and one
 * application can hold several households.
 */
export interface Household {
  /** The applicant whose record holds the household's shared expenses. */
  ownerId: string;
  memberIds: string[];
}

/**
 * Picks the applicant whose record holds a household's shared expenses. Ownership follows the data:
 * whichever member already holds the expense record keeps it, otherwise the conventional owner (the
 * primary applicant of the pair, else loan order).
 *
 * Declaration state deliberately plays no part. Signing a declaration does not move the expense
 * record, so letting it pick the owner would switch ownership to a partner holding nothing — the UI
 * would read an empty record, report the household incomplete, and save a second one. Nor does the
 * result depend on who is logged in, so both partners resolve the same owner.
 */
const resolveOwnerId = (
  memberIds: string[],
  applicants: ApplicantWithRelationships[],
  holdsExpenseRecord: (applicantId: string) => boolean,
): string => {
  const holder = memberIds.find((id) => holdsExpenseRecord(id));
  if (holder) return holder;

  const primary = memberIds.find((id) => applicants.find((a) => a.id === id)?.type === ApplicantType.PRIMARY);
  return primary ?? memberIds[0];
};

/**
 * Groups every applicant on the loan into households, in loan order. Each applicant appears in
 * exactly one household.
 *
 * `holdsExpenseRecord` reports whether an applicant's financials already carry the household's
 * expense record, which is what decides ownership — not whether they have declared.
 */
export const resolveHouseholds = (
  applicants: ApplicantWithRelationships[],
  holdsExpenseRecord: (applicantId: string) => boolean,
): Household[] => {
  const assigned = new Set<string>();
  const households: Household[] = [];

  for (const applicant of applicants) {
    if (assigned.has(applicant.id)) continue;

    const partnerId = resolveMarriedPartnerId(applicant.id, applicants);
    const memberIds = partnerId ? [applicant.id, partnerId] : [applicant.id];
    memberIds.forEach((id) => assigned.add(id));

    households.push({ ownerId: resolveOwnerId(memberIds, applicants, holdsExpenseRecord), memberIds });
  }

  return households;
};

export const findHousehold = (applicantId: string | undefined, households: Household[]): Household | undefined =>
  applicantId ? households.find((h) => h.memberIds.includes(applicantId)) : undefined;

/**
 * Names a household by its members — "Darcy Heller & Beep Boop" for a couple, or just the one name
 * for a single applicant. Expenses belong to the household rather than to any borrower entity (a
 * couple can span a company and a trust), so the card is labelled by who it covers, with no entity
 * heading or entity role.
 */
export const householdLabel = (household: Household, nameOf: (applicantId: string) => string): string =>
  household.memberIds.map(nameOf).filter(Boolean).join(' & ');
